import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

type RefType = {
  current: HTMLElement | null;
};

/**
 * Animates the section title with a fade-in and slide-up effect
 */
export const animateTitle = (
  titleRef: RefType,
  sectionRef: RefType,
  delay: number = 0
): void => {
  if (!titleRef.current || !sectionRef.current) return;

  gsap.fromTo(
    titleRef.current,
    { opacity: 0, y: 20 },
    {
      opacity: 1,
      y: 0,
      duration: 0.7,
      delay,
      ease: "power3.out",
      scrollTrigger: {
        id: "founderSection-title", // Add this ID
        trigger: sectionRef.current,
        start: "top 70%",
        once: true,
      },
    }
  );
};

/**
 * Animates the founder's image with a scale and fade effect
 */
export const animateImage = (
  imageRef: RefType,
  sectionRef: RefType,
  delay: number = 0
): void => {
  if (!imageRef.current || !sectionRef.current) return;

  gsap.fromTo(
    imageRef.current,
    { opacity: 0, scale: 0.9 },
    {
      opacity: 1,
      scale: 1,
      duration: 0.7,
      delay,
      ease: "power3.out",
      scrollTrigger: {
        id: "founderSection-image", // Add this ID
        trigger: sectionRef.current,
        start: "top 70%",
        once: true,
      },
    }
  );
};

/**
 * Animates the bio text with a fade-in and slide-up effect
 */
export const animateBio = (
  bioRef: RefType,
  sectionRef: RefType,
  delay: number = 0.3
): void => {
  if (!bioRef.current || !sectionRef.current) return;

  gsap.fromTo(
    bioRef.current,
    { opacity: 0, y: 30 },
    {
      opacity: 1,
      y: 0,
      duration: 0.7,
      delay,
      ease: "power3.out",
      scrollTrigger: {
        id: "founderSection-bio", // Add this ID
        trigger: sectionRef.current,
        start: "top 70%",
        once: true,
      },
    }
  );
};

/**
 * Animates social media icons with a staggered fade-in
 */
export const animateSocial = (
  socialRef: RefType,
  sectionRef: RefType,
  delay: number = 0.5
): void => {
  if (!socialRef.current || !sectionRef.current) return;

  const socialIcons = socialRef.current.querySelectorAll("a");

  gsap.fromTo(
    socialIcons,
    { opacity: 0, y: 20 },
    {
      opacity: 1,
      y: 0,
      duration: 0.5,
      stagger: 0.1,
      delay,
      ease: "power3.out",
      scrollTrigger: {
        id: "founderSection-social",
        trigger: socialRef.current,
        start: "top 90%",
        once: true,
      },
    }
  );
};

/**
 * Cleans up all ScrollTrigger instances
 */
export const cleanupScrollTriggers = (): void => {
  ScrollTrigger.getAll()
    .filter((trigger) => trigger.vars.id?.startsWith("founderSection"))
    .forEach((trigger) => trigger.kill());
};
